import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { App } from "@capacitor/app";
import { PRODUCT_CAMERA_EVENT, beginProductCamera, completeProductCamera, readProductCameraDraft } from "./productCameraDraft";

let recoveryListener;
const cancelled = error => /cancel|취소/i.test(String(error?.message || ""));

function publish(requestId, data, error, restored = false) {
  const pending = readProductCameraDraft();
  if (pending?.requestId === requestId) {
    try {
      // Keep only the latest timing/outcome; never persist photos, user data or tokens in diagnostics.
      localStorage.setItem("dworks.product-camera-diagnostic.v1", JSON.stringify({
        at: Date.now(), restored, elapsedMs: Math.max(0, Date.now() - pending.createdAt),
        outcome: data?.dataUrl ? "success" : cancelled(error) ? "cancelled" : "error"
      }));
    } catch {}
  }
  const result = data?.dataUrl?.startsWith("data:image/jpeg;base64,")
    ? { status: "complete", imageUrl: data.dataUrl }
    : cancelled(error)
      ? { status: "cancelled" }
      : { status: "error", message: "촬영 사진을 불러오지 못했습니다. 작성 내용은 유지됩니다. 다시 촬영하거나 갤러리에서 선택해 주세요." };
  try {
    if (completeProductCamera(requestId, result)) window.dispatchEvent(new Event(PRODUCT_CAMERA_EVENT));
  } catch {
    window.dispatchEvent(new Event(PRODUCT_CAMERA_EVENT));
    throw new Error("사진을 임시 저장하지 못했습니다. 다시 시도해 주세요.");
  }
}

export function initProductCameraRecovery() {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("App")) return Promise.resolve();
  if (!recoveryListener) {
    recoveryListener = App.addListener("appRestoredResult", event => {
      if (event.pluginId !== "Camera" || event.methodName !== "getPhoto") return;
      const pending = readProductCameraDraft();
      if (!pending || pending.status !== "pending") return;
      try { publish(pending.requestId, event.success ? event.data : null, event.error, true); }
      catch { /* Keep the draft available if persistent storage is unavailable. */ }
    }).catch(error => { recoveryListener = null; throw error; });
  }
  return recoveryListener;
}

export async function captureProductPhoto(ownerId, draft) {
  if (!Capacitor.isPluginAvailable("Camera") || !Capacitor.isPluginAvailable("App")) {
    throw new Error("카메라 촬영을 사용하려면 앱을 최신 버전으로 업데이트해 주세요. 갤러리 첨부는 계속 사용할 수 있습니다.");
  }
  await initProductCameraRecovery();
  const requestId = beginProductCamera(ownerId, draft);
  try {
    const photo = await Camera.getPhoto({ source: CameraSource.Camera, resultType: CameraResultType.DataUrl,
      width: 2048, height: 2048, quality: 88, correctOrientation: true, allowEditing: false, saveToGallery: false });
    publish(requestId, photo);
  } catch (error) {
    publish(requestId, null, error);
  }
}

